<script setup lang="ts">
  /**
   * Login View
   *
   * Authentication page for all domains.
   * Redirects to appropriate dashboard after login based on role and domain.
   */
  import { ref, computed } from 'vue'
  import { useRoute, useRouter } from 'vue-router'
  import { useAuth } from '../../composables/useAuth'
  import { useAuthStore } from '../../stores/auth'
  import { authService } from '../../services/auth.service'
  import { redirectToSignInOnce } from '../../services/sign-in-redirect'
  import { getAppDomain, type AppDomain } from '../../utils/domain'
  import AppAuthState from '../../components/auth/AppAuthState.vue'
  import type { AppAuthStateKind } from '../../components/auth/app-auth-state'
  import logoHorizontal from '../../assets/logos/claimshield-horizontal.png'
  import logoHorizontalReverse from '../../assets/logos/claimshield-horizontal-reverse.png'

  const currentDomain: AppDomain = getAppDomain()

  const { login, isLoading } = useAuth()

  const email = ref('')
  const password = ref('')

  /**
   * `?denied=app` is set by the nav guard when it signs someone out because no
   * route in THIS app admits their role — an ops-only account opening the
   * client portal, say. Without a reason on screen they see a plain login form
   * seconds after typing the right password, conclude the password failed, and
   * retry into the same wall. Says which app rather than which role: the role
   * is our vocabulary, the address bar is theirs.
   */
  const route = useRoute()
  const router = useRouter()
  const authStore = useAuthStore()
  const staffOidc = computed(() => authStore.mode === 'oidc')
  /** `?signed_out=1`: the sign-in server's sign-out just landed here. */
  const signedOut = !!route.query.signed_out
  /** `?failed=1`: the sign-in server sent the person back and the server refused the result. */
  const failed = !!route.query.failed

  /**
   * After a no-access screen or a sign-out, sign-in asks for a password again
   * (`fresh`): the sign-in server may still remember the account that was just
   * refused or signed out, and would otherwise hand it straight back.
   */
  function startStaffSignIn(fresh = false) {
    window.location.assign(authService.staffLoginUrl({ fresh }))
  }

  /**
   * `?unreachable=1`: the guard could not restore the sign-in because the
   * server did not answer (network drop, deploy restart). The sign-in is kept,
   * so the useful action is "try again", not a password form (ops audit V6).
   */
  const retrying = ref(false)
  // `retrying` holds the state while a retry runs: retryRestore() clears the store's
  // flag before it tries again, which would otherwise flash a different screen.
  const unreachable = computed(() => !!route.query.unreachable && (authStore.restoreUnreachable || retrying.value))
  /**
   * Identity sign-in has nothing to ask on this page, so a signed-out visit goes
   * straight to the sign-in server (Ahmed's walk, 2026-10-22). The page stays
   * for a denial, an unreachable server, just after sign-out, or when the loop
   * breaker holds.
   */
  const redirecting = ref(staffOidc.value && !route.query.denied && !unreachable.value && !signedOut && !failed &&
    redirectToSignInOnce(authService.staffLoginUrl()))
  async function retryConnection(): Promise<boolean> {
    retrying.value = true
    try {
      await authStore.retryRestore()
      if (authStore.isAuthenticated) {
        const target = typeof route.query.redirect === 'string' ? route.query.redirect : '/'
        await router.replace(target)
        return true
      }
      return false
    } finally {
      retrying.value = false
    }
  }

  /**
   * Identity sign-in has no form here, only a state (spec b-app-auth-states, 2026-10-23):
   * the bridge while the tab leaves for sign-in, then signed out, no access, could not
   * connect, or — when the loop breaker holds — could not finish.
   */
  const oidcState = computed<AppAuthStateKind>(() => {
    if (redirecting.value) return 'bridge'
    if (unreachable.value) return 'unreachable'
    if (route.query.denied) return 'denied'
    if (signedOut) return 'signed-out'
    return 'failed'
  })
  function oidcAction(): Promise<boolean> | void {
    if (oidcState.value === 'unreachable') return retryConnection()
    startStaffSignIn(oidcState.value === 'denied' || oidcState.value === 'signed-out')
  }

  const error = ref(
    route.query.denied
      ? 'That account does not have access to this app. Sign in with a different account, or ask your ClaimShield contact to grant access.'
      : ''
  )

  /**
   * `getAppDomain()` returns 'marketing' for any host that is not `portal.*` or
   * `ops.*` — and `apply.claimshieldpro.com` (packages/marketing) routes this
   * very view. So the default branch is not a dev nicety: it decided that a
   * DRIVER signing in to the application app was greeted with "Operations" and
   * "Internal Ops · Where compliance gets done". Ops framing is opt-in by
   * hostname; every other host gets the client-facing voice, because a staff
   * member seeing client copy costs nothing and the reverse is an internal
   * voice leaking to a customer.
   */
  const isOps = computed(() => currentDomain === 'ops')

  /**
   * The overline is shown to staff only, and that is the whole rule.
   *
   * "Operations" orients someone who juggles several internal tools. "Client
   * Portal" oriented nobody — it is OUR taxonomy, coined to disambiguate our
   * three portals, and a carrier signing in thinks "I'm signing in to
   * ClaimShield". On apply.claimshieldpro.com it was worse than noise: that
   * person is an applicant, not a client. The wordmark already says whose
   * product this is, so customer-facing surfaces get heading + subline and
   * nothing above them.
   */
  const domainLabel = computed(() => 'Operations')

  async function handleSubmit() {
    error.value = ''

    if (!email.value || !password.value) {
      error.value = 'Please enter your email and password'
      return
    }

    const failure = await login({
      username: email.value,
      password: password.value,
    })

    if (failure) {
      error.value = failure
    }
  }

</script>

<template>
  <!-- Staff identity mode exists only in ops (stores/auth: VITE_STAFF_OIDC and not the portal's
       identity mode), so the app is named outright, not guessed from the hostname. -->
  <AppAuthState
    v-if="staffOidc"
    :state="oidcState"
    app-label="Operations"
    app-name="ClaimShield Operations"
    :action="oidcAction"
  />
  <div v-else class="login-page flex min-h-screen bg-white">
    <!-- Full-page container -->
    <div class="flex w-full">

      <!-- Left panel — gradient mesh with rounded inset -->
      <div class="mesh-panel relative m-3 hidden w-7/12 rounded-3xl lg:flex lg:flex-col lg:justify-between lg:px-10 lg:pb-10 lg:pt-8">
        <div class="mesh-bg rounded-3xl" />

        <!-- Top logo -->
        <div class="relative z-10 pt-2">
          <img
            :src="logoHorizontalReverse"
            alt="ClaimShield"
            class="h-9 w-auto"
          />
        </div>

        <!-- Bottom text — domain-aware -->
        <div class="relative z-10">
          <template v-if="isOps">
            <p class="text-xs font-semibold uppercase tracking-[0.08em] text-white/85">Internal Ops</p>
            <p class="mt-2 text-2xl font-semibold leading-tight text-white">
              Where compliance<br />gets done.
            </p>
          </template>
          <template v-else>
            <p class="text-2xl font-semibold leading-tight text-white">
              Your fleet. Your data.<br />Always current.
            </p>
          </template>
        </div>
      </div>

      <!-- Right panel — form.
           The canon's focus-page anatomy caps the content column at 440px and
           centres it (playbook 07); without the cap the 5/12 column just grows
           with the viewport — measured 509px at 1512w, 721px at 2000w. -->
      <div class="flex w-full flex-col justify-center px-8 py-10 lg:w-5/12 lg:px-14 lg:py-14">
       <div class="mx-auto w-full max-w-[440px]">
        <!-- Mobile logo -->
        <div class="mb-6 lg:hidden">
          <img
            :src="logoHorizontal"
            alt="ClaimShield"
            class="h-8 w-auto"
          />
        </div>

        <!-- Header -->
        <div class="mb-6">
          <span
            v-if="isOps"
            class="text-xs font-semibold uppercase tracking-[0.08em] text-primary-700"
          >
            {{ domainLabel }}
          </span>
          <h1 class="mt-1 text-xl font-semibold text-secondary-900">Welcome back</h1>
          <!-- secondary-400 measured 2.56:1 on white; secondary-500 is 4.76:1. -->
          <p class="mt-1 text-sm text-secondary-500">
            Sign in to access your dashboard.
          </p>
        </div>

        <!-- Error -->
        <div
          v-if="unreachable"
          role="alert"
          class="mb-5 rounded-lg bg-amber-50 px-4 py-3 text-sm text-amber-900"
        >
          <p class="font-medium">Couldn't reach ClaimShield.</p>
          <p class="mt-1">You're still signed in. Check your connection, then try again.</p>
          <button
            type="button"
            class="mt-2 inline-flex items-center gap-2 rounded-lg bg-amber-100 px-3 py-1.5 font-medium hover:bg-amber-200 disabled:opacity-60"
            :disabled="retrying"
            @click="retryConnection"
          >
            <i :class="retrying ? 'pi pi-spin pi-spinner' : 'pi pi-refresh'" />
            Try again
          </button>
        </div>
        <div
          v-if="error"
          class="mb-5 flex items-center gap-2 rounded-lg bg-danger-50 px-4 py-3 text-sm text-danger-700"
        >
          <i class="pi pi-exclamation-circle text-danger-500" />
          <span>{{ error }}</span>
        </div>

        <!-- Form -->
        <form @submit.prevent="handleSubmit" class="space-y-4">
          <div>
            <label for="email" class="mb-1.5 block text-sm font-medium text-secondary-700">
              Your email
            </label>
            <PInputText
              id="email"
              v-model="email"
              type="email"
              placeholder="you@company.com"
              class="light-input w-full"
              :disabled="isLoading"
              autocomplete="email"
            />
          </div>

          <div>
            <label for="password" class="mb-1.5 block text-sm font-medium text-secondary-700">
              Password
            </label>
            <PPassword
              id="password"
              v-model="password"
              :feedback="false"
              toggleMask
              placeholder="Enter your password"
              class="light-input w-full"
              inputClass="w-full"
              :disabled="isLoading"
              :inputProps="{ autocomplete: 'current-password' }"
            />
            <!-- `inputProps`, not a bare attribute: PrimeVue 4.5's Password puts
                 plain attributes on its wrapper div, so the hint never reached the
                 <input>. Same defect as ResetPasswordView (P2, 2026-09-22). -->

          </div>

          <!-- No "Remember me": it changed nothing, both tokens always persist
               in localStorage (ops audit 2026-10-02, shell #13). -->
          <div class="flex items-center justify-end">
            <!-- It is a link AND the recovery path, but rendered as muted grey at
                 2.56:1. Links get the anchor purple (11.6:1) and read as clickable. -->
            <RouterLink
              to="/forgot-password"
              class="text-sm font-medium text-primary-700 transition-colors hover:text-primary-800 hover:underline"
            >
              Forgot password?
            </RouterLink>
          </div>

          <PButton
            type="submit"
            label="Sign in"
            :loading="isLoading"
            class="primary-btn w-full"
          />
        </form>

        <!-- Footer — secondary-300 measured 1.48:1 on white, so the legal links
             were effectively invisible. secondary-500 is 4.76:1. -->
        <div class="mt-8 text-center text-xs text-secondary-500">
          <p>
            &copy; {{ new Date().getFullYear() }} ClaimShield &middot; Safety &amp; Compliance Services
          </p>
          <p class="mt-1.5">
            <a
              href="https://claimshieldpro.com/terms"
              target="_blank"
              rel="noopener noreferrer"
              class="underline underline-offset-2 transition-colors hover:text-primary-700"
            >Terms of Service</a>
            <span class="mx-1.5">&middot;</span>
            <a
              href="https://claimshieldpro.com/privacy"
              target="_blank"
              rel="noopener noreferrer"
              class="underline underline-offset-2 transition-colors hover:text-primary-700"
            >Privacy Policy</a>
          </p>
        </div>
       </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* ── Page ── */
.login-page {
  background: #fff;
  min-height: 100vh;
}

/* ── Left mesh gradient panel ── */
.mesh-panel {
  position: relative;
  overflow: hidden;
  border-radius: 1.5rem;
}

.mesh-bg {
  position: absolute;
  inset: 0;
  background: var(--cs-purple);
  /* Layered radial gradients for mesh effect */
  background:
    radial-gradient(ellipse at 20% 50%, rgba(107, 79, 175, 0.9) 0%, transparent 60%),
    radial-gradient(ellipse at 70% 20%, rgba(139, 92, 246, 0.7) 0%, transparent 50%),
    radial-gradient(ellipse at 40% 80%, rgba(59, 130, 246, 0.5) 0%, transparent 50%),
    radial-gradient(ellipse at 80% 70%, rgba(168, 85, 247, 0.6) 0%, transparent 45%),
    radial-gradient(ellipse at 10% 20%, rgba(236, 72, 153, 0.3) 0%, transparent 50%),
    linear-gradient(135deg, var(--cs-purple-dark), var(--cs-purple-light));
  filter: blur(0px);
}

/* Soft blur overlay on the mesh for smoothness */
.mesh-bg::after {
  content: '';
  position: absolute;
  inset: 0;
  backdrop-filter: blur(40px);
  -webkit-backdrop-filter: blur(40px);
}

/* ── Animated mesh — visible color shift ── */
.mesh-bg {
  background-size: 200% 200% !important;
  animation: mesh-shift 6s ease-in-out infinite;
}

@keyframes mesh-shift {
  0%, 100% { background-position: 0% 50%; }
  25% { background-position: 100% 25%; }
  50% { background-position: 50% 100%; }
  75% { background-position: 25% 0%; }
}


/* ── Light inputs ── */
input.light-input,
.light-input :deep(input) {
  background: #fff !important;
  border: 1px solid theme('colors.secondary.200') !important;
  border-radius: 0.5rem !important;
  padding: 0.5rem 0.75rem !important;
  font-size: 0.875rem !important;
  color: theme('colors.secondary.800') !important;
  transition: border-color 0.2s, box-shadow 0.2s !important;
}

input.light-input:focus,
.light-input :deep(input:focus) {
  border-color: var(--cs-purple-light) !important;
  /* Canon focus glow is .16, not .10 — knowledge/brand/tokens.md "Elevation + focus". */
  box-shadow: 0 0 0 3px rgba(107, 79, 175, 0.16) !important;
  outline: none !important;
}

input.light-input::placeholder,
.light-input :deep(input::placeholder) {
  color: theme('colors.secondary.400') !important;
}

.light-input :deep(.p-password-toggle-mask-btn) {
  color: theme('colors.secondary.400');
}

.light-input :deep(.p-password-toggle-mask-btn:hover) {
  color: theme('colors.secondary.500');
}

/* ── Primary button — canon solid purple (tokens.md "Buttons"; the gradient is retired) ── */
.primary-btn {
  background: var(--cs-purple) !important;
  border: none !important;
  color: #fff !important;
  border-radius: 0.5rem !important;
  /* 44px min — Apple's comfortable touch target. Was 0.55rem padding = 39px. */
  padding: 0.6875rem 1.25rem !important;
  min-height: 44px !important;
  font-weight: 500 !important;
  font-size: 0.875rem !important;
  transition: background-color 0.15s !important;
}

.primary-btn:hover,
.primary-btn:active {
  background: var(--cs-purple-dark) !important;
}
</style>
